import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, Database, ChevronRight } from 'lucide-react';
import { formatINR } from './boqCalc';

/**
 * Shared Item & Cost Sheet picker. Calls onSelect(item, variant).
 * Used by both the project BOQ editor and the BOQ Template Builder.
 */
export default function ItemCostSheetPicker({ open, onOpenChange, onSelect }) {
  const [dbItems, setDbItems] = useState([]);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const res = await api.get('/db-items'); setDbItems(res.data || []); }
      catch { /* ignore */ }
    })();
    setSearch(''); setExpanded({});
  }, [open]);

  const filtered = dbItems.filter(item => {
    if (!search) return true;
    const q = search.toLowerCase();
    if (item.item_name.toLowerCase().includes(q)) return true;
    if (item.variants?.some(v => v.specification?.toLowerCase().includes(q))) return true;
    return false;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-['Manrope'] flex items-center gap-2 text-base">
            <Database size={16} className="text-[#3A8C8C]" /> Item &amp; Cost Sheet
          </DialogTitle>
        </DialogHeader>
        <div className="relative mt-2">
          <Search size={14} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input placeholder="Search items or specifications..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 rounded-lg border-[#E8E6E1] text-sm" data-testid="boq-picker-search" />
        </div>
        <div className="flex-1 overflow-y-auto mt-3 space-y-1 max-h-[400px]" data-testid="boq-picker-list">
          {filtered.map(item => (
            <div key={item.id} className="border border-[#E8E6E1] rounded-lg overflow-hidden">
              <button onClick={() => setExpanded(p => ({ ...p, [item.id]: !p[item.id] }))}
                className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#FAFAF8] transition-colors text-left"
                data-testid={`boq-picker-item-${item.sr_no}`}>
                <ChevronRight size={14} className={`text-[#6B7280] transition-transform ${expanded[item.id] ? 'rotate-90' : ''}`} />
                <span className="text-[10px] text-[#6B7280] font-mono w-6">{item.sr_no}.</span>
                <span className="text-sm font-medium text-[#2C2C2E] flex-1">{item.item_name}</span>
                <span className="text-[10px] text-[#6B7280]">{item.variants?.filter(v => !v.archived).length || 0} specs</span>
              </button>
              {expanded[item.id] && (
                <div className="border-t border-[#E8E6E1] bg-[#FAFAF8]/50">
                  {(item.variants || []).filter(v => !v.archived).map(variant => (
                    <button key={variant.id} onClick={() => onSelect(item, variant)}
                      className="w-full flex items-center gap-3 px-3 pl-10 py-2 hover:bg-[#3A8C8C]/5 transition-colors text-left border-b border-[#E8E6E1] last:border-0"
                      data-testid={`boq-picker-variant-${variant.id}`}>
                      <div className="flex-1 min-w-0"><p className="text-xs text-[#2C2C2E]">{variant.specification}</p></div>
                      <span className="text-[10px] text-[#6B7280] shrink-0">{variant.uom}</span>
                      <span className="text-xs font-semibold text-[#3A8C8C] shrink-0 w-20 text-right">Rs {formatINR(variant.rate)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="text-center py-8">
              <Database size={24} strokeWidth={1.5} className="text-[#D4D0C8] mx-auto mb-2" />
              <p className="text-sm text-[#6B7280]">No matching items found</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
